// src/components/TripReplayMap.jsx
import React, { useState, useEffect, useRef } from "react";
import { MapContainer, TileLayer, Marker, Popup, Polyline, useMap } from "react-leaflet";
import L from "leaflet";
import LiveVehicleMarker from "./LiveVehicleMarker";
import RecenterButton from "./RecenterButton";

// Custom pickup marker (green)
const pickupIcon = new L.DivIcon({
  html: `
    <div style="
      background: linear-gradient(135deg, #10b981, #059669);
      width: 36px;
      height: 36px;
      border-radius: 50% 50% 50% 0;
      transform: rotate(-45deg);
      border: 3px solid white;
      box-shadow: 0 4px 12px rgba(16, 185, 129, 0.5);
      display: flex;
      align-items: center;
      justify-content: center;
    ">
      <span style="transform: rotate(45deg); color: white; font-weight: bold; font-size: 14px;">P</span>
    </div>
  `,
  className: "custom-pickup-marker",
  iconSize: [36, 36],
  iconAnchor: [18, 36],
  popupAnchor: [0, -36],
});

// Custom destination marker (red)
const destinationIcon = new L.DivIcon({
  html: `
    <div style="
      background: linear-gradient(135deg, #ef4444, #dc2626);
      width: 36px;
      height: 36px;
      border-radius: 50% 50% 50% 0;
      transform: rotate(-45deg);
      border: 3px solid white;
      box-shadow: 0 4px 12px rgba(239, 68, 68, 0.5);
      display: flex;
      align-items: center;
      justify-content: center;
    ">
      <span style="transform: rotate(45deg); color: white; font-weight: bold; font-size: 14px;">D</span>
    </div>
  `,
  className: "custom-destination-marker",
  iconSize: [36, 36],
  iconAnchor: [18, 36],
  popupAnchor: [0, -36],
});

// Auto-fit map bounds component (only once initially)
const FitBounds = ({ pickup, dropoff }) => {
  const map = useMap();
  const hasFitted = useRef(false);
  
  useEffect(() => {
    if (pickup && dropoff && !hasFitted.current) {
      const bounds = L.latLngBounds([pickup, dropoff]);
      map.fitBounds(bounds, { padding: [50, 50] });
      hasFitted.current = true; // Only fit once
    }
  }, [map, pickup, dropoff]);
  
  return null;
};

const fetchRoute = async (start, end) => {
  const apiKey = process.env.REACT_APP_ORS_KEY;
  if (!apiKey) return null;

  const url = `https://api.openrouteservice.org/v2/directions/driving-car?api_key=${apiKey}&start=${start[1]},${start[0]}&end=${end[1]},${end[0]}`;

  try {
    const res = await fetch(url);
    const data = await res.json();
    const feature = data.features?.[0];
    if (feature?.geometry?.coordinates) {
      return feature.geometry.coordinates.map(([lng, lat]) => [lat, lng]);
    }
  } catch (e) {
    console.error("ORS route error:", e);
  }
  return null;
};

const AnimatedCar = ({ track, isPlaying, speed, vehicleType }) => {
  const [positionIndex, setPositionIndex] = useState(0);
  const intervalRef = useRef(null);

  useEffect(() => {
    if (!isPlaying || !track || track.length === 0) {
      if (intervalRef.current) clearInterval(intervalRef.current);
      return;
    }

    const totalDuration = 8000; // 8 seconds base
    const intervalMs = (totalDuration / track.length) / speed;

    intervalRef.current = setInterval(() => {
      setPositionIndex((prev) => {
        const next = prev + 1;
        if (next >= track.length) {
          clearInterval(intervalRef.current);
          return prev;
        }
        return next;
      });
    }, intervalMs);

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [isPlaying, track, speed]);

  if (!track || positionIndex >= track.length) return null;

  const pos = track[positionIndex];
  return <LiveVehicleMarker position={{ lat: pos[0], lng: pos[1] }} vehicleType={vehicleType} />;
};

const ReplayControls = ({ isPlaying, setIsPlaying, speed, setSpeed, onReset }) => {
  return (
    <div className="flex items-center gap-3 mt-3 bg-white p-3 rounded-lg shadow-sm border">
      <button
        onClick={() => setIsPlaying(!isPlaying)}
        className="px-4 py-1.5 bg-blue-600 text-white rounded text-sm font-medium flex items-center gap-1.5"
      >
        {isPlaying ? "Pause" : "Play"}
      </button>

      <select
        value={speed}
        onChange={(e) => setSpeed(Number(e.target.value))}
        className="text-xs border rounded px-2 py-1"
      >
        <option value={0.5}>0.5x</option>
        <option value={1}>1x</option>
        <option value={2}>2x</option>
        <option value={5}>5x</option>
      </select>

      <button
        onClick={onReset}
        className="text-xs text-gray-600 hover:text-gray-900 underline"
      >
        Reset
      </button>
    </div>
  );
};

const TripReplayMap = ({ pickup, dropoff, pickupLabel, dropoffLabel, vehicleType }) => {
  const [track, setTrack] = useState([]);
  const [isPlaying, setIsPlaying] = useState(false);
  const [speed, setSpeed] = useState(1);
  const [routeLoading, setRouteLoading] = useState(true);

  const handleReset = async () => {
    setIsPlaying(false);
    setTrack([]);
    setRouteLoading(true);
    const route = await fetchRoute(pickup, dropoff);
    if (route) setTrack(route);
    setRouteLoading(false);
  };

  useEffect(() => {
    handleReset();
  }, [pickup, dropoff]);

  // Calculate center point between pickup and dropoff
  const center = [
    (pickup[0] + dropoff[0]) / 2,
    (pickup[1] + dropoff[1]) / 2
  ];

  return (
    <div className="space-y-3">
      {/* Legend */}
      <div className="flex items-center gap-6 px-2">
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 rounded-full bg-gradient-to-r from-green-500 to-emerald-600 border-2 border-white shadow"></div>
          <span className="text-sm font-medium text-gray-700">Pickup</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 rounded-full bg-gradient-to-r from-red-500 to-rose-600 border-2 border-white shadow"></div>
          <span className="text-sm font-medium text-gray-700">Destination</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-8 h-1 bg-blue-500 rounded"></div>
          <span className="text-sm font-medium text-gray-700">Route</span>
        </div>
      </div>

      {/* Map */}
      <div className="relative">
        {routeLoading && (
          <div className="absolute inset-0 bg-white/80 backdrop-blur-sm z-10 flex items-center justify-center rounded-2xl">
            <div className="flex items-center gap-3">
              <div className="w-6 h-6 border-3 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
              <span className="text-gray-600 font-medium">Loading route...</span>
            </div>
          </div>
        )}
        <MapContainer
          center={center}
          zoom={13}
          style={{ height: "350px", borderRadius: "16px" }}
          key={`${pickup.join(",")}-${dropoff.join(",")}`}
        >
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          <RecenterButton position={center} zoom={13} />
          {/* Auto-fit bounds */}
          <FitBounds pickup={pickup} dropoff={dropoff} />
          
          {/* Pickup Marker */}
          <Marker position={pickup} icon={pickupIcon}>
            <Popup>
              <div className="text-center">
                <div className="font-bold text-green-700">📍 Pickup Location</div>
                <div className="text-sm text-gray-600">{pickupLabel || `${pickup[0].toFixed(4)}, ${pickup[1].toFixed(4)}`}</div>
              </div>
            </Popup>
          </Marker>
          
          {/* Destination Marker */}
          <Marker position={dropoff} icon={destinationIcon}>
            <Popup>
              <div className="text-center">
                <div className="font-bold text-red-700">🎯 Destination</div>
                <div className="text-sm text-gray-600">{dropoffLabel || `${dropoff[0].toFixed(4)}, ${dropoff[1].toFixed(4)}`}</div>
              </div>
            </Popup>
          </Marker>
          
          {/* Route Line */}
          {track.length > 0 && (
            <>
              <Polyline 
                positions={track} 
                color="#3b82f6" 
                weight={5} 
                opacity={0.8}
                dashArray="10, 5"
              />
              <AnimatedCar track={track} isPlaying={isPlaying} speed={speed} vehicleType={vehicleType} />
            </>
          )}
        </MapContainer>
      </div>
      
      {/* Controls */}
      <ReplayControls
        isPlaying={isPlaying}
        setIsPlaying={setIsPlaying}
        speed={speed}
        setSpeed={setSpeed}
        onReset={handleReset}
      />
    </div>
  );
};

export default TripReplayMap;